/**
 * Stale-while-revalidate cache for the game Redis reads.
 *
 * The rule is "cache, then recache on load": a view paints whatever was cached for its keys the
 * instant it mounts, and every load still goes to the source and replaces the cached value when
 * the fresh answer arrives. Nothing is ever served from the cache *instead of* the source, so the
 * data on screen is only ever as stale as one in-flight request — the badge (DataFreshness)
 * says which state it is in.
 *
 * Two tiers: a memory map (instant, per tab) and IndexedDB (survives reloads; the values here are
 * too large for localStorage). Without IndexedDB (private windows, tests) it degrades to memory.
 * Entries older than MAX_AGE_MS are ignored and pruned. `swrCache.clear()` runs on logout.
 */

export interface CachedEntry<T> {
  value: T;
  storedAt: number;
}

export interface PersistentStore {
  get(key: string): Promise<CachedEntry<unknown> | undefined>;
  set(key: string, entry: CachedEntry<unknown>): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
  /** Removes every entry stored before `before` (ms since epoch). */
  prune(before: number): Promise<void>;
}

const DB_NAME = 'argus-swr';
const DB_VERSION = 1;
const STORE = 'entries';
/** Cached values older than this are never shown, even as a placeholder. */
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

class IndexedDbStore implements PersistentStore {
  private db: Promise<IDBDatabase> | null = null;

  private open(): Promise<IDBDatabase> {
    if (!this.db) {
      this.db = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
        request.onblocked = () => reject(new Error('IndexedDB open blocked'));
      });
      this.db.catch(() => {
        this.db = null;
      });
    }
    return this.db;
  }

  private async tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await this.open();
    return requestToPromise(run(db.transaction(STORE, mode).objectStore(STORE)));
  }

  async get(key: string): Promise<CachedEntry<unknown> | undefined> {
    const entry = await this.tx('readonly', (s) => s.get(key));
    return entry && typeof entry === 'object' && 'value' in entry && typeof (entry as CachedEntry<unknown>).storedAt === 'number' ? (entry as CachedEntry<unknown>) : undefined;
  }

  async set(key: string, entry: CachedEntry<unknown>): Promise<void> {
    await this.tx('readwrite', (s) => s.put(entry, key));
  }

  async delete(key: string): Promise<void> {
    await this.tx('readwrite', (s) => s.delete(key));
  }

  async clear(): Promise<void> {
    await this.tx('readwrite', (s) => s.clear());
  }

  async prune(before: number): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const store = db.transaction(STORE, 'readwrite').objectStore(STORE);
      const cursor = store.openCursor();
      cursor.onsuccess = () => {
        const c = cursor.result;
        if (!c) return resolve();
        const entry = c.value as Partial<CachedEntry<unknown>> | undefined;
        if (!entry || typeof entry.storedAt !== 'number' || entry.storedAt < before) c.delete();
        c.continue();
      };
      cursor.onerror = () => reject(cursor.error ?? new Error('IndexedDB prune failed'));
    });
  }
}

/** Memory-only fallback when IndexedDB is unavailable. */
export class NoopStore implements PersistentStore {
  async get(): Promise<undefined> {
    return undefined;
  }
  async set(): Promise<void> {}
  async delete(): Promise<void> {}
  async clear(): Promise<void> {}
  async prune(): Promise<void> {}
}

/** In-memory PersistentStore, for tests. */
export class MapStore implements PersistentStore {
  readonly entries = new Map<string, CachedEntry<unknown>>();
  async get(key: string) {
    return this.entries.get(key);
  }
  async set(key: string, entry: CachedEntry<unknown>) {
    this.entries.set(key, entry);
  }
  async delete(key: string) {
    this.entries.delete(key);
  }
  async clear() {
    this.entries.clear();
  }
  async prune(before: number) {
    for (const [k, e] of this.entries) if (e.storedAt < before) this.entries.delete(k);
  }
}

function createDefaultStore(): PersistentStore {
  try {
    if (typeof indexedDB !== 'undefined' && indexedDB) return new IndexedDbStore();
  } catch {
    // fall through
  }
  return new NoopStore();
}

export class SwrCache {
  private readonly memory = new Map<string, CachedEntry<unknown>>();
  private readonly inflight = new Map<string, Promise<unknown>>();
  private pruned = false;

  constructor(
    private readonly store: PersistentStore,
    private readonly maxAgeMs: number = MAX_AGE_MS,
    private readonly now: () => number = () => Date.now()
  ) {}

  private usable(entry: CachedEntry<unknown> | undefined | null): entry is CachedEntry<unknown> {
    return !!entry && this.now() - entry.storedAt <= this.maxAgeMs;
  }

  private pruneOnce(): void {
    if (this.pruned) return;
    this.pruned = true;
    this.store.prune(this.now() - this.maxAgeMs).catch(() => undefined);
  }

  /** The memory tier only (synchronous). */
  peek<T>(key: string): CachedEntry<T> | null {
    const entry = this.memory.get(key);
    return this.usable(entry) ? (entry as CachedEntry<T>) : null;
  }

  /** Memory first, then the persistent tier; null when nothing usable is cached. */
  async get<T>(key: string): Promise<CachedEntry<T> | null> {
    this.pruneOnce();
    const hit = this.peek<T>(key);
    if (hit) return hit;
    try {
      const stored = await this.store.get(key);
      if (this.usable(stored)) {
        this.memory.set(key, stored);
        return stored as CachedEntry<T>;
      }
    } catch {
      // A broken persistent tier only costs the placeholder.
    }
    return null;
  }

  /** Writes both tiers. The persistent write is best-effort. */
  async set<T>(key: string, value: T): Promise<CachedEntry<T>> {
    const entry: CachedEntry<T> = { value, storedAt: this.now() };
    this.memory.set(key, entry);
    await this.store.set(key, entry).catch(() => undefined);
    return entry;
  }

  async delete(key: string): Promise<void> {
    this.memory.delete(key);
    await this.store.delete(key).catch(() => undefined);
  }

  async clear(): Promise<void> {
    this.memory.clear();
    await this.store.clear().catch(() => undefined);
  }

  /** One in-flight fetch per key: concurrent loads of the same key share the request. */
  fetchDeduped<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const running = this.inflight.get(key) as Promise<T> | undefined;
    if (running) return running;
    const p = fetcher().finally(() => {
      if (this.inflight.get(key) === p) this.inflight.delete(key);
    });
    this.inflight.set(key, p);
    return p;
  }
}

export const swrCache = new SwrCache(createDefaultStore());

// ---------------------------------------------------------------------------------------------
// loadSwr: the "cache, then recache on load" step a view runs for its set of reads.
// ---------------------------------------------------------------------------------------------

export interface SwrApplyMeta {
  /** True for the cached placeholder, false for the fresh answer. */
  fromCache: boolean;
  /** When the value being applied was read from the source. */
  storedAt: number;
}

export interface SwrRead<T> {
  key: string;
  fetch: () => Promise<T>;
  /** Puts the value on screen. Called once for the cached value (if any) and once for the fresh one. */
  apply: (value: T, meta: SwrApplyMeta) => void | Promise<void>;
}

/** Typed constructor: `read(key, fetch, apply)` infers T from `fetch` so `apply` is typed. */
export function read<T>(key: string, fetch: () => Promise<T>, apply: SwrRead<T>['apply']): SwrRead<T> {
  return { key, fetch, apply };
}

export type SwrPhase =
  | 'idle'
  /** Nothing cached; waiting on the source. */
  | 'loading'
  /** Cached values are on screen; the source is being re-read. */
  | 'cached'
  /** Everything on screen came from the source just now. */
  | 'live'
  /** Cached values are on screen and the re-read failed. */
  | 'stale'
  /** Nothing usable to show; the re-read failed (or answered definitively, e.g. not found). */
  | 'error';

export interface SwrStatus {
  phase: SwrPhase;
  /** Oldest read time among the cached values on screen (cached/stale). */
  cachedAt: number | null;
  /** When the source last answered in full (live). */
  updatedAt: number | null;
  error: string | null;
}

export const SWR_IDLE: SwrStatus = { phase: 'idle', cachedAt: null, updatedAt: null, error: null };

export interface LoadSwrOptions {
  /** Skip the cached placeholder (still stores the fresh answer). */
  force?: boolean;
  /** Return false once the caller has moved on (unmounted, changed cluster); nothing is applied after that. */
  isCurrent?: () => boolean;
  onStatus?: (status: SwrStatus) => void;
  /** Fires after every cached value was applied, i.e. the view has something to show. */
  onCached?: () => void;
  cache?: SwrCache;
}

export interface LoadSwrResult {
  status: SwrStatus;
  /** One settled result per read, in order; empty when the caller moved on before the source answered. */
  results: PromiseSettledResult<unknown>[];
  /** True when `isCurrent()` turned false mid-load: nothing further was applied and the caller should not touch its state either. */
  superseded: boolean;
}

/**
 * Errors that are an answer rather than a failure (e.g. "not found"). A definitive rejection
 * evicts the key and is reported as `error`, never `stale`: cached data must not outlive a
 * source that says the data is gone.
 */
export function isDefinitiveError(e: unknown): boolean {
  return typeof e === 'object' && e !== null && (e as { definitive?: unknown }).definitive === true;
}

const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export async function loadSwr(reads: SwrRead<any>[], options: LoadSwrOptions = {}): Promise<LoadSwrResult> {
  const cache = options.cache ?? swrCache;
  const isCurrent = options.isCurrent ?? (() => true);
  let status: SwrStatus = { phase: 'loading', cachedAt: null, updatedAt: null, error: null };
  const setStatus = (patch: Partial<SwrStatus>) => {
    status = { ...status, ...patch };
    options.onStatus?.(status);
  };
  setStatus({});

  let showedCache = false;
  if (!options.force && reads.length > 0) {
    const hits = await Promise.all(reads.map((r) => cache.get<unknown>(r.key)));
    if (!isCurrent()) return { status, results: [], superseded: true };
    // Only a complete set is shown: a half-cached page would paint one panel with old data and
    // the rest empty, which reads as "this player has no economy log" rather than "loading".
    if (hits.every((h) => h !== null)) {
      for (let i = 0; i < reads.length; i++) {
        const hit = hits[i]!;
        await reads[i].apply(hit.value, { fromCache: true, storedAt: hit.storedAt });
      }
      if (!isCurrent()) return { status, results: [], superseded: true };
      showedCache = true;
      setStatus({ phase: 'cached', cachedAt: Math.min(...hits.map((h) => h!.storedAt)) });
      options.onCached?.();
    }
  }

  const results = await Promise.allSettled(reads.map((r) => cache.fetchDeduped(r.key, r.fetch)));
  if (!isCurrent()) return { status, results, superseded: true };

  const now = Date.now();
  let error: string | null = null;
  let definitive = false;
  for (let i = 0; i < reads.length; i++) {
    const result = results[i];
    if (result.status === 'fulfilled') {
      void cache.set(reads[i].key, result.value);
      await reads[i].apply(result.value, { fromCache: false, storedAt: now });
      if (!isCurrent()) return { status, results, superseded: true };
    } else {
      error ??= messageOf(result.reason);
      if (isDefinitiveError(result.reason)) {
        definitive = true;
        void cache.delete(reads[i].key);
      }
    }
  }

  if (error === null) setStatus({ phase: 'live', updatedAt: now, error: null });
  else if (showedCache && !definitive) setStatus({ phase: 'stale', error });
  else setStatus({ phase: 'error', error });
  return { status, results, superseded: false };
}
